import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import { generateOrganizationSchema } from '../utils/schema';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const About = () => {
  const [companyInfo, setCompanyInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/company-info`)
      .then(res => setCompanyInfo(res.data))
      .catch(err => console.error('Error:', err));
  }, []);

  return (
    <div>
      <SEO 
        title="Licensed NYC Pest Control Company"
        description="Reclaim Pest Control Inc — licensed, insured, NYS DEC-certified exterminator serving Queens, Brooklyn, Manhattan, Bronx, and all of NYC & Long Island. Same-day service. Call 646-209-9955."
        keywords="licensed NYC pest control, NYC exterminator company, NYS DEC certified pest control, professional exterminators NYC, pest control company Queens"
        schema={generateOrganizationSchema()}
        canonical="https://www.reclaimpestcontrol.com/about"
      />
      <section className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="about-h1">Licensed NYC Pest Control Company</h1>
          <p className="text-xl">Reclaim Pest Control Inc &mdash; trusted, licensed, and insured pest control across NYC and Long Island.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <img 
                src="/images/hazmat-person.webp" 
                alt="Professional Pest Control Technician" 
                className="w-full max-w-md mx-auto rounded-lg shadow-lg"
              />
            </div>
            
            <h2 className="text-3xl font-bold mb-6">Who We Are</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              {companyInfo?.about || "Reclaim Pest Control is a trusted and reliable local pest control company in Queens, NY, serving Manhattan, Brooklyn, New York City, Long Island and surrounding counties. Our experienced team is dedicated to providing you with the highest quality pest control service using the latest products and methods."}
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-12">
              We take pride in being able to provide our customers with the best pest control solutions for their specific needs. Whether you&apos;re dealing with rodents, bed bugs, cockroaches, termites, or any other pest, we have the expertise and equipment to eliminate them quickly and effectively.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-blue-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
                <p className="text-gray-700">{companyInfo?.mission || "Our mission is to create safe, pest-free environments for homes and businesses in New York. We are dedicated to delivering exceptional service through innovative, eco-friendly solutions."}</p>
              </div>
              <div className="bg-green-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
                <p className="text-gray-700">{companyInfo?.vision || "Our vision is to be the leading provider of pest management solutions, recognized for our commitment to innovation, sustainability, and customer care."}</p>
              </div>
            </div>

            <h2 className="text-3xl font-bold mb-6">Why Choose Us?</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <div className="flex items-start">
                <div className="text-3xl mr-4">✓</div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Licensed & Insured</h4>
                  <p className="text-gray-600">Fully licensed and insured for your protection and peace of mind.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-3xl mr-4">✓</div>
                <div>
                  <h4 className="font-bold text-lg mb-2">NYS DEC Certified</h4>
                  <p className="text-gray-600">Licensed by the New York State Department of Environmental Conservation. Certified applicators on every job.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-3xl mr-4">✓</div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Eco-Friendly Solutions</h4>
                  <p className="text-gray-600">Safe, environmentally responsible treatments for your family and pets.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-3xl mr-4">✓</div>
                <div>
                  <h4 className="font-bold text-lg mb-2">24/7 Emergency Service</h4>
                  <p className="text-gray-600">Available around the clock for urgent pest problems.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-3xl mr-4">✓</div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Experienced Team</h4>
                  <p className="text-gray-600">Highly trained and certified pest control professionals.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-3xl mr-4">✓</div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Satisfaction Guaranteed</h4>
                  <p className="text-gray-600">We stand behind our work with a 100% satisfaction guarantee.</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg mb-12">
              <h2 className="text-3xl font-bold mb-6">Service Areas</h2>
              <p className="text-gray-700 mb-4">We proudly serve the following areas:</p>
              <div className="grid md:grid-cols-3 gap-4">
                {companyInfo?.service_areas ? companyInfo.service_areas.map((area, idx) => (
                  <div key={idx} className="bg-white p-3 rounded text-center font-medium">{area}</div>
                )) : null}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Work With Us?</h2>
          <p className="text-xl mb-8">Experience the Reclaim Pest Control difference today!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:6462099955" className="bg-white text-blue-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition">Call (646) 209-9955</a>
            <Link to="/contact" className="bg-green-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-green-700 transition">Request Free Quote</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;